import React, { useMemo, useState } from 'react';

import CardDialog from '~/components/CardDialog';
import Button from '~/components/core/Atomic/Buttons/Button';
import SimpleSearchField from '~/components/core/SimpleSearchField';
import SkeletonTable from '~/components/core/Skeletons/SkeletonTable';
import SortableTable from '~/components/core/Tables/SortableTable';
import Text from '~/components/core/TextComponents/Text';
import useDebouncedValue from '~/hooks/useDebouncedValue';

import type { ConfiguredTable, ConfiguredTableSection, PolicyWithConfiguredTables } from './types';

const ROWS_PER_PAGE = 25;
// Filtering scans every cell of every row synchronously, so on large tables we wait until the
// user pauses typing before re-filtering. This keeps the input responsive and the main thread free.
const SEARCH_DEBOUNCE_MS = 300;
// Cap the skeleton size so large tables don't render an unwieldy placeholder while filtering.
const SEARCH_SKELETON_MAX_ROWS = 10;
const SEARCH_SKELETON_MAX_COLUMNS = 10;
// Cell keys are namespaced so a user column_key of "id" can't collide with the
// synthetic row id that SortableTable requires for its row keys.
const CELL_KEY_PREFIX = 'cell_';

interface ConfiguredTablesSectionProps {
  policy?: PolicyWithConfiguredTables;
}

interface ConfiguredTableCardProps {
  table: ConfiguredTable;
}

interface ConfiguredTableDialogProps {
  table: ConfiguredTable;
  onClose: () => void;
}

const ConfiguredTableDialog: React.FC<ConfiguredTableDialogProps> = ({ table, onClose }) => {
  // searchText updates immediately on each keystroke (keeps the input responsive); the actual
  // filtering runs against the debounced value so we don't re-scan the whole table per character.
  const [searchText, setSearchText] = useState('');
  const debouncedSearch = useDebouncedValue(searchText, SEARCH_DEBOUNCE_MS);

  const columns = useMemo(
    () =>
      table.columns.map((column) => ({
        id: `${CELL_KEY_PREFIX}${column.column_key}`,
        label: column.display_name,
      })),
    [table]
  );

  // SortableTable requires a stable row.id (used for row/cell react keys). Cell values
  // live under prefixed keys so they can never clash with that synthetic id. Built once per
  // table so a search keystroke doesn't re-allocate the entire dataset.
  const rows = useMemo(
    () =>
      (table.rows ?? []).map((row, index) => {
        const mappedRow: { id: number; [cellKey: string]: string | number } = { id: index };
        table.columns.forEach((column) => {
          mappedRow[`${CELL_KEY_PREFIX}${column.column_key}`] = String(
            (row as Record<string, unknown>)[column.column_key] ?? ''
          );
        });
        return mappedRow;
      }),
    [table]
  );

  const filteredRows = useMemo(() => {
    const normalizedSearch = debouncedSearch.trim().toLowerCase();
    if (!normalizedSearch) {
      return rows;
    }
    return rows.filter((row) =>
      columns.some((column) =>
        String(row[column.id] ?? '')
          .toLowerCase()
          .includes(normalizedSearch)
      )
    );
  }, [rows, columns, debouncedSearch]);

  // A filter pass is pending while the input has outrun the debounced value the table is showing.
  const isSearching = searchText.trim() !== debouncedSearch.trim();

  return (
    <CardDialog isDialog title={table.display_name} maxWidth="lg" fullWidth onClose={onClose}>
      <div className="mb-8 flex justify-start">
        <div className="w-[30%] min-w-[220px] max-w-[360px]">
          <SimpleSearchField placeholder="Search" onChange={setSearchText} renderClearButton />
        </div>
      </div>
      {isSearching ? (
        <SkeletonTable
          rowsCount={Math.min(rows.length, SEARCH_SKELETON_MAX_ROWS)}
          columnsCount={Math.min(columns.length, SEARCH_SKELETON_MAX_COLUMNS)}
        />
      ) : (
        <SortableTable
          // Remount only when the applied (debounced) search changes so the table's internal
          // auto-pagination page resets to 0 (prevents landing on an out-of-range empty page after
          // the result set shrinks). Keying on the debounced value avoids a full remount per keystroke.
          key={debouncedSearch}
          columns={columns}
          rows={filteredRows}
          autoPaginateRowsPerPage={ROWS_PER_PAGE}
          emptyStateComponent={<span>No matching rows</span>}
        />
      )}
    </CardDialog>
  );
};

const ConfiguredTableCard: React.FC<ConfiguredTableCardProps> = ({ table }) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  return (
    <>
      <div className="flex items-center justify-between rounded border border-slate-200 bg-slate-100 px-16 py-12">
        <Text variant={Text.VARIANTS.LG} weight={Text.WEIGHTS.MEDIUM}>
          {table.display_name}
        </Text>
        <Button variant="text" color="primary" onClick={() => setIsDialogOpen(true)}>
          Show
        </Button>
      </div>
      {isDialogOpen && <ConfiguredTableDialog table={table} onClose={() => setIsDialogOpen(false)} />}
    </>
  );
};

const ConfiguredTablesSectionCard: React.FC<{ section: ConfiguredTableSection }> = ({ section }) => {
  const tables: ConfiguredTable[] = Array.isArray(section.tables) ? section.tables : [];

  return (
    <div className="m-8 overflow-hidden rounded-md shadow-xs">
      <CardDialog title={section.custom_name}>
        <div className="flex flex-col gap-8">
          {tables.map((table, tableIndex) => (
            <ConfiguredTableCard key={`${table.table_key ?? table.display_name}-${tableIndex}`} table={table} />
          ))}
        </div>
      </CardDialog>
    </div>
  );
};

const ConfiguredTablesSection: React.FC<ConfiguredTablesSectionProps> = ({ policy }) => {
  // Defensive: backend may serialize the column as null for policies created before this feature.
  const rawSections = policy?.configured_tables?.sections;
  // Only render sections that actually carry tables — an empty section adds no value on the Policy tab.
  const sections: ConfiguredTableSection[] = (Array.isArray(rawSections) ? rawSections : []).filter(
    (section) => Array.isArray(section.tables) && section.tables.length > 0
  );

  if (sections.length === 0) {
    return null;
  }

  return (
    <div className="max-w-[50%] grow-0 basis-1/2">
      {sections.map((section, index) => (
        <ConfiguredTablesSectionCard key={section.key ?? `${section.custom_name}-${index}`} section={section} />
      ))}
    </div>
  );
};

export default ConfiguredTablesSection;
